import { useEffect, useState } from 'react';
import { Plus, FileText, MoreHorizontal, Eye, CheckCircle, XCircle, Send, Trash2, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  ConfirmDialog,
} from '@/components/common';
import {
  purchaseOrdersApi,
  PurchaseOrder,
  CreatePurchaseOrderDto,
  QueryPurchaseOrderParams,
  suppliersApi,
  Supplier,
  inventoryApi,
  InventoryItem,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { useAuth } from '@/context/AuthContext';

const statusMap: Record<string, 'draft' | 'pending' | 'approved' | 'rejected' | 'processing' | 'completed'> = {
  DRAFT: 'draft',
  SUBMITTED: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ORDERED: 'processing',
  PARTIALLY_RECEIVED: 'processing',
  RECEIVED: 'completed',
  CANCELLED: 'rejected',
};

interface LineItem {
  itemId: string;
  item?: InventoryItem;
  quantity: number;
  unitPrice: number;
}

interface POFormData {
  supplierId: string;
  expectedDate: string;
  currency: string;
  notes: string;
  items: LineItem[];
}

const emptyFormData: POFormData = {
  supplierId: '',
  expectedDate: '',
  currency: 'USD',
  notes: '',
  items: [],
};

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<POFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // Action dialog
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    order?: PurchaseOrder;
    action?: 'submit' | 'issue' | 'cancel';
  }>({ open: false });
  const [processing, setProcessing] = useState(false);

  const fetchOrders = async (params?: QueryPurchaseOrderParams) => {
    try {
      setLoading(true);
      const response = await purchaseOrdersApi.getAll(params);
      setOrders(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const response = await suppliersApi.getAll({ isActive: true });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      setLoadingItems(true);
      const response = await inventoryApi.getAll({ isActive: true });
      setInventoryItems(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    const params: QueryPurchaseOrderParams = {
      search: searchQuery || undefined,
    };
    if (statusFilter !== 'all') {
      params.status = statusFilter as PurchaseOrder['status'];
    }
    fetchOrders(params);
  }, [searchQuery, statusFilter]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const openCreateForm = async () => {
    setFormData(emptyFormData);
    setFormOpen(true);
    await Promise.all([fetchSuppliers(), fetchInventoryItems()]);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemId: '', quantity: 1, unitPrice: 0 }],
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    const newItems = [...formData.items];
    if (field === 'itemId') {
      const selectedItem = inventoryItems.find(item => item.id === value);
      newItems[index] = {
        ...newItems[index],
        itemId: value as string,
        item: selectedItem,
        unitPrice: selectedItem?.unitCost || 0,
      };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.supplierId) {
      toast.error('Please select a supplier');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const invalidItems = formData.items.filter(item => !item.itemId || item.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error('Please complete all line items');
      return;
    }

    try {
      setSaving(true);

      const payload: CreatePurchaseOrderDto = {
        companyId: user?.companyId || '',
        supplierId: formData.supplierId,
        expectedDate: formData.expectedDate || undefined,
        currency: formData.currency,
        notes: formData.notes || undefined,
        items: formData.items.map(item => ({
          itemId: item.itemId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
      };

      await purchaseOrdersApi.create(payload);
      toast.success('Purchase order created successfully');

      setFormOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async () => {
    if (!actionDialog.order || !actionDialog.action) return;

    try {
      setProcessing(true);
      switch (actionDialog.action) {
        case 'submit':
          await purchaseOrdersApi.submit(actionDialog.order.id);
          toast.success('PO submitted for approval');
          break;
        case 'issue':
          await purchaseOrdersApi.issue(actionDialog.order.id);
          toast.success('PO issued to supplier');
          break;
        case 'cancel':
          await purchaseOrdersApi.cancel(actionDialog.order.id);
          toast.success('PO cancelled');
          break;
      }
      setActionDialog({ open: false });
      fetchOrders();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const getActionDialogContent = () => {
    switch (actionDialog.action) {
      case 'submit':
        return {
          title: 'Submit for Approval',
          description: 'This will send the purchase order for approval. Are you sure?',
          confirmLabel: 'Submit',
          variant: 'default' as const,
        };
      case 'issue':
        return {
          title: 'Issue to Supplier',
          description: 'This will issue the purchase order to the supplier. Are you sure?',
          confirmLabel: 'Issue',
          variant: 'default' as const,
        };
      case 'cancel':
        return {
          title: 'Cancel Purchase Order',
          description: 'This will cancel the purchase order. This action cannot be undone.',
          confirmLabel: 'Cancel PO',
          variant: 'destructive' as const,
        };
      default:
        return { title: '', description: '', confirmLabel: '', variant: 'default' as const };
    }
  };

  // Calculate metrics
  const totalValue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
  const pendingOrders = orders.filter(o => o.status === 'SUBMITTED').length;
  const orderedCount = orders.filter(o => ['ORDERED', 'PARTIALLY_RECEIVED'].includes(o.status)).length;

  const columns: Column<PurchaseOrder>[] = [
    {
      key: 'poNumber',
      header: 'PO Number',
      sortable: true,
      render: (order) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <FileText className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{order.poNumber}</div>
            <div className="text-sm text-muted-foreground">
              {order._count?.items || 0} items
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (order) => (
        <span className="text-muted-foreground">{order.supplier?.name || '-'}</span>
      ),
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      render: (order) => (
        <span className="font-medium">{formatCurrency(order.totalAmount)}</span>
      ),
    },
    {
      key: 'orderDate',
      header: 'Order Date',
      render: (order) => (
        <span className="text-muted-foreground">{formatDate(order.orderDate)}</span>
      ),
    },
    {
      key: 'expectedDate',
      header: 'Expected Date',
      render: (order) => (
        <span className="text-muted-foreground">{formatDate(order.expectedDate)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (order) => (
        <StatusBadge status={statusMap[order.status] || 'draft'} />
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (order) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/purchase-orders/${order.id}`)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            {order.status === 'DRAFT' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setActionDialog({ open: true, order, action: 'submit' })}>
                  <Send className="mr-2 h-4 w-4" />
                  Submit for Approval
                </DropdownMenuItem>
              </>
            )}
            {order.status === 'APPROVED' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setActionDialog({ open: true, order, action: 'issue' })}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Issue to Supplier
                </DropdownMenuItem>
              </>
            )}
            {order.status === 'DRAFT' && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setActionDialog({ open: true, order, action: 'cancel' })}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Cancel
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Purchase Orders"
        description="Manage procurement and purchase orders"
        breadcrumbs={[{ label: 'Purchase Orders' }]}
        actions={
          <Button onClick={openCreateForm}>
            <Plus className="mr-2 h-4 w-4" />
            Create PO
          </Button>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total Orders"
          value={orders.length}
          icon={<FileText className="h-5 w-5" />}
        />
        <MetricCard
          label="Total Value"
          value={formatCurrency(totalValue)}
        />
        <MetricCard
          label="Pending Approval"
          value={pendingOrders}
          trend={pendingOrders > 0 ? { direction: 'neutral', value: '', text: 'needs attention' } : undefined}
        />
        <MetricCard
          label="In Progress"
          value={orderedCount}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search orders..."
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="SUBMITTED">Pending Approval</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="ORDERED">Ordered</SelectItem>
            <SelectItem value="PARTIALLY_RECEIVED">Partially Received</SelectItem>
            <SelectItem value="RECEIVED">Received</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        data={orders}
        columns={columns}
        loading={loading}
        onRowClick={(order) => navigate(`/purchase-orders/${order.id}`)}
        emptyTitle="No purchase orders found"
        emptyDescription="Get started by creating your first purchase order."
        emptyAction={
          <Button onClick={openCreateForm}>
            <Plus className="mr-2 h-4 w-4" />
            Create PO
          </Button>
        }
      />

      {/* Create PO Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Purchase Order</SheetTitle>
            <SheetDescription>
              Select a supplier and add items to create a new purchase order.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Supplier Selection */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Order Details</h4>
              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier *</Label>
                <Select
                  value={formData.supplierId}
                  onValueChange={(value) => setFormData({ ...formData, supplierId: value })}
                  disabled={loadingSuppliers}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingSuppliers ? 'Loading...' : 'Select supplier'} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} ({supplier.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expectedDate">Expected Delivery</Label>
                  <Input
                    id="expectedDate"
                    type="date"
                    value={formData.expectedDate}
                    onChange={(e) => setFormData({ ...formData, expectedDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>

            <Separator />

            {/* Line Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-muted-foreground">Line Items</h4>
                <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                  <Plus className="mr-2 h-3 w-3" />
                  Add Item
                </Button>
              </div>

              {formData.items.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-center">
                  <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No items added yet.</p>
                  <Button type="button" variant="link" size="sm" onClick={handleAddItem}>
                    Add your first item
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-start justify-between">
                        <Label className="text-xs text-muted-foreground">Item {index + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Select
                          value={item.itemId}
                          onValueChange={(value) => handleItemChange(index, 'itemId', value)}
                          disabled={loadingItems}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={loadingItems ? 'Loading...' : 'Select item'} />
                          </SelectTrigger>
                          <SelectContent>
                            {inventoryItems.map((invItem) => (
                              <SelectItem key={invItem.id} value={invItem.id}>
                                {invItem.name} ({invItem.sku})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Unit Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div className="text-right text-sm font-medium">
                        Subtotal: {formatCurrency(item.quantity * item.unitPrice)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.items.length > 0 && (
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-medium">Total</span>
                  <span className="text-lg font-semibold">{formatCurrency(calculateTotal())}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Creating...' : 'Create Purchase Order'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Action Confirmation Dialog */}
      <ConfirmDialog
        open={actionDialog.open}
        onOpenChange={(open) => setActionDialog({ open })}
        {...getActionDialogContent()}
        onConfirm={handleAction}
        loading={processing}
      />
    </div>
  );
}
